<template>
  <div class="register-container" :class="{ 'popup-mode': usePopupMode }">
    <router-link
      v-if="showLogoLink"
      to="/"
      class="logo-link"
      aria-label="Home page"
    >
      <img src="/images/eddy.svg" alt="" aria-hidden="true" />
    </router-link>
    <!-- Left side -->
    <div v-if="showLeftPanel" class="left-panel">
      <div class="left-content">
        <img
          src="/images/register-illustration.svg"
          alt=""
          class="illustration"
          aria-hidden="true"
        />
        <p class="promotional-text">
          Turn your classroom into an <br />
          online Makerspace today!
        </p>

        <BaseButton
          variant="white"
          class="login-redirect-button"
          @click="goToLogin"
        >
          Already signed up? Sign In here
        </BaseButton>
      </div>
    </div>

    <!-- Right side -->
    <div :class="['right-panel', { 'full-width': !showLeftPanel }]">
      <div class="form-content">
        <template v-if="emailUsed">
          <div class="email-used-container">
            <div class="email-used-content">
              <h2 class="color-primary">Email already in use</h2>
              <p>
                It seems that you've already signed up for Eddy using your
                e-mail <br />
                {{ email }}.
              </p>
              <BaseButton variant="primary" @click="goToLogin">
                Sign In with this email
              </BaseButton>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="title-container">
            <div class="title-wrapper">
              <h1 class="title">Sign Up</h1>
              <p class="mobile-signin-text">
                Already signed up?
                <button class="link-style" @click="goToLogin">
                  Sign In here
                </button>
              </p>
            </div>
          </div>

          <div class="register-form-container">
            <!-- Input fields -->
            <div class="name-fields-container">
              <div class="name-fields-wrapper">
                <BaseInput
                  type="text"
                  label="First Name*"
                  placeholder="Input First Name"
                  v-model="firstName"
                  autocomplete="given-name"
                  :error="isFirstNameError"
                  :errorMessage="firstNameErrorMessage"
                  class="name-input"
                  :showInputError="false"
                />
                <BaseInput
                  type="text"
                  label="Last Name*"
                  placeholder="Input Last Name"
                  v-model="lastName"
                  autocomplete="family-name"
                  :error="isLastNameError"
                  :errorMessage="lastNameErrorMessage"
                  class="name-input"
                  :showInputError="false"
                />
              </div>
            </div>

            <div class="form-inputs">
              <div class="input-wrapper">
                <BaseInput
                  type="text"
                  label="Email*"
                  placeholder="Input Email"
                  v-model="email"
                  autocomplete="email"
                  class="input-field"
                  :error="isEmailError"
                  :errorMessage="emailErrorMessage"
                  :showInputError="false"
                />
              </div>
            </div>
            <div class="form-inputs">
              <div class="input-wrapper">
                <BaseInput
                  type="password"
                  label="Password*"
                  placeholder="Input Password"
                  v-model="password"
                  autocomplete="new-password"
                  class="input-field"
                  @input="validatePasswordInput"
                  :error="isPasswordError"
                  :errorMessage="passwordErrorMessage"
                  :showInputError="false"
                />
                <PasswordValidationList
                  :password="password"
                  :validations="validations"
                />
              </div>
            </div>
            <div class="form-inputs">
              <div class="input-wrapper">
                <BaseInput
                  type="password"
                  label="Confirm Password*"
                  placeholder="Confirm your password"
                  v-model="retypePassword"
                  autocomplete="new-password"
                  :class="['input-field', { 'with-password': password }]"
                  :error="isRetypePasswordError"
                  :errorMessage="retypePasswordErrorMessage"
                  :showInputError="false"
                />
              </div>
              <div
                v-if="requestErrorMessage"
                class="error-message"
                role="alert"
                aria-live="assertive"
              >
                {{ requestErrorMessage }}
              </div>
            </div>

            <label class="newsletter-opt-in">
              <span class="newsletter-checkbox-wrap">
                <input
                  v-model="subscribeNewsletter"
                  type="checkbox"
                  class="newsletter-checkbox"
                />
                <span class="newsletter-checkbox-visual" aria-hidden="true" />
              </span>
              <span class="newsletter-label">
                Send me a news & product updates. Unsubscribe anytime
              </span>
            </label>

            <!-- reCAPTCHA -->
            <div
              v-if="showRecaptcha"
              id="register-recaptcha"
              ref="recaptchaContainer"
              class="recaptcha-container"
            ></div>

            <!-- Sign Up Button with loading state -->
            <div class="action-buttons">
              <AuthButton
                @click="signUp"
                :loading="loading"
                :showDisabledStyle="isFormEmpty"
                loadingText="Signing Up..."
                text="Sign Up"
                className="signup-button"
              />

              <div class="or-divider">
                <span class="line"></span>
                <span class="text">Or</span>
                <span class="line"></span>
              </div>

              <div class="social-buttons">
                <button class="google-button" @click="signUpWithGoogle">
                  <img
                    src="/images/google-logo.svg"
                    alt="Google Icon"
                    aria-hidden="true"
                  />
                  Continue with Google
                </button>
                <button class="facebook-button" disabled>
                  <img
                    src="/images/facebook.svg"
                    alt="Facebook Icon"
                    class="facebook-icon"
                    aria-hidden="true"
                  />
                  Continue with Facebook
                </button>
              </div>

              <div class="footer-text-container">
                <p class="footer-text">
                  By creating an account you agree with our
                  <a
                    class="signup-footer-links"
                    @click="isTermsOfServiceModalOpen = true"
                    >Terms of Service</a
                  >,
                  <a
                    class="signup-footer-links"
                    @click="isPrivacyPolicyModalOpen = true"
                    >Privacy Policy</a
                  >, and our default
                  <a
                    class="signup-footer-links"
                    @click="isNotificationSettingModalOpen = true"
                    >Notification Settings</a
                  >.
                </p>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
  <TermsOfServiceModal
    :isOpen="isTermsOfServiceModalOpen"
    @close="isTermsOfServiceModalOpen = false"
  />
  <PrivacyPolicyModal
    :isOpen="isPrivacyPolicyModalOpen"
    @close="isPrivacyPolicyModalOpen = false"
  />
  <NotificationSettingModal
    :isOpen="isNotificationSettingModalOpen"
    @close="isNotificationSettingModalOpen = false"
  />
</template>

<script setup>
import BaseButton from "@/components/common/BaseButton.vue";
import AuthButton from "@/components/common/AuthButton.vue";
import NotificationSettingModal from "@/components/modal/NotificationSettingModal.vue";
import PrivacyPolicyModal from "@/components/modal/PrivacyPolicyModal.vue";
import TermsOfServiceModal from "@/components/modal/TermsOfServiceModal.vue";
import PasswordValidationList from "@/components/PasswordValidationList.vue";
import useRecaptcha from "@/composeables/useRecaptcha";
import { useUserState } from "@/composeables/useUserState";
import authService from "@/services/authService";
import cookiesService from "@/services/cookiesService";
import userService from "@/services/userService";
import { validateEmail, validatePassword } from "@/utils";
import { isAxiosError } from "axios";
import {
  computed,
  defineEmits,
  defineProps,
  onMounted,
  onUnmounted,
  ref,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import BaseInput from "../components/BaseInput.vue";
import { getSafeAuthRedirect } from "@/utils/authRedirect";
import { startGoogleLogin } from "@/services/googleLoginService";

// Props
const props = defineProps({
  // Controls whether to show the left promotional panel
  showLeftPanel: {
    type: Boolean,
    default: true,
  },
  // Controls whether to show the logo link in top-left
  showLogoLink: {
    type: Boolean,
    default: true,
  },
  // Controls whether to show language selector
  showLanguageSelector: {
    type: Boolean,
    default: true,
  },
  // Controls whether to show reCAPTCHA
  showRecaptcha: {
    type: Boolean,
    default: true,
  },
  // Controls whether to redirect after successful registration
  enableRedirect: {
    type: Boolean,
    default: true,
  },
  // Default redirect URL after successful registration
  defaultRedirectUrl: {
    type: String,
    default: "/email-verification",
  },
  // Redirect URL to include in registration request for verify email link
  redirectUrl: {
    type: String,
    default: "",
  },
  // Controls whether to use popup mode for Google login (for modal usage)
  usePopupMode: {
    type: Boolean,
    default: false,
  },
});

// Emits
const emit = defineEmits([
  "register-success",
  "register-error",
  "goto-login",
  "google-register",
  "goto-email-verification",
]);

const router = useRouter();
const route = useRoute();
const { updateUserState } = useUserState();

const emailUsed = ref(false);

const firstName = ref("");
const lastName = ref("");
const email = ref("");
const password = ref("");
const retypePassword = ref("");
const subscribeNewsletter = ref(false);
const loading = ref(false);

const isTermsOfServiceModalOpen = ref(false);
const isPrivacyPolicyModalOpen = ref(false);
const isNotificationSettingModalOpen = ref(false);

const isFirstNameError = ref(false);
const firstNameErrorMessage = ref("");

const isLastNameError = ref(false);
const lastNameErrorMessage = ref("");

const isEmailError = ref(false);
const emailErrorMessage = ref("");

const isPasswordError = ref(false);
const passwordErrorMessage = ref("");

const isRetypePasswordError = ref(false);
const retypePasswordErrorMessage = ref("");

const requestErrorMessage = ref("");

const isFormEmpty = computed(() => {
  return (
    !firstName.value.trim() ||
    !lastName.value.trim() ||
    !email.value.trim() ||
    !password.value.trim() ||
    !retypePassword.value.trim()
  );
});

const recaptchaContainer = ref(null);
let stopGoogleLogin = () => {};

const {
  recaptchaToken,
  getRecaptchaToken,
  renderRecaptcha,
  resetRecaptcha,
  initialize,
} = useRecaptcha(recaptchaContainer, process.env.VUE_APP_RECAPTCHA_SITE_KEY);

onMounted(async () => {
  if (props.showRecaptcha) {
    try {
      await initialize();
      await new Promise((resolve) => setTimeout(resolve, 100));
      if (recaptchaContainer.value) {
        await renderRecaptcha();
      }
    } catch (error) {
      console.error("Failed to initialize reCAPTCHA:", error);
    }
  }
});

onUnmounted(() => {
  stopGoogleLogin();
});

function validateForm() {
  // Reset error states
  isFirstNameError.value = !firstName.value;
  firstNameErrorMessage.value = isFirstNameError.value
    ? "First name required"
    : "";

  isLastNameError.value = !lastName.value;
  lastNameErrorMessage.value = isLastNameError.value
    ? "Last name required"
    : "";

  const { isValid, message } = validateEmail(email.value);
  isEmailError.value = !isValid;
  emailErrorMessage.value = isEmailError.value ? message : "";

  const validatePasswordResult = validatePassword(password.value);
  isPasswordError.value = !validatePasswordResult.isValid;
  passwordErrorMessage.value = validatePasswordResult.message;

  if (!retypePassword.value) {
    isRetypePasswordError.value = true;
    retypePasswordErrorMessage.value = "Confirm password required";
  } else if (password.value && password.value !== retypePassword.value) {
    isRetypePasswordError.value = true;
    retypePasswordErrorMessage.value = "Passwords do not match";
  }

  return !(
    isFirstNameError.value ||
    isLastNameError.value ||
    isEmailError.value ||
    isPasswordError.value ||
    isRetypePasswordError.value
  );
}

const validations = ref({
  lowercase: false,
  uppercase: false,
  specialChar: false,
  length: false,
});

function clearError() {
  isFirstNameError.value = false;
  isLastNameError.value = false;
  isEmailError.value = false;
  isPasswordError.value = false;
  isRetypePasswordError.value = false;
  firstNameErrorMessage.value = "";
  lastNameErrorMessage.value = "";
  emailErrorMessage.value = "";
  passwordErrorMessage.value = "";
  retypePasswordErrorMessage.value = "";
}

function validatePasswordInput() {
  const result = validatePassword(password.value);
  validations.value = result.validations;
}

function handleEmailVerificationRequired() {
  localStorage.setItem("registeredEmail", email.value);
  if (props.enableRedirect) {
    router.push(props.defaultRedirectUrl);
  }
}

async function handleGoogleLoginSuccess(result) {
  try {
    const authToken = await cookiesService.getCookie("authToken");
    if (!authToken?.token) throw new Error("Google login session unavailable");
    await updateUserState();
    const response = await userService.getProfile();
    const userData = response.data?.data;
    emit("register-success", {
      email: userData?.email || "",
      firstName: userData?.first_name || "",
      lastName: userData?.last_name || "",
      isGoogleAuth: true,
      isMergedAccount: result.isMergedAccount,
    });
    if (props.enableRedirect) {
      router.push(getSafeAuthRedirect(result.redirectTo));
    }
  } catch {
    console.error("Failed to complete Google registration.");
    emit("register-error", "Failed to complete Google registration");
  }
}

async function signUp() {
  clearError();
  if (!validateForm()) {
    return;
  }

  // Handle reCAPTCHA if enabled
  let token = null;
  if (props.showRecaptcha) {
    token = await getRecaptchaToken();
    if (!token) {
      requestErrorMessage.value = "Please complete the reCAPTCHA verification";
      return;
    }
  }

  loading.value = true;
  try {
    const data = {
      firstname: firstName.value,
      lastname: lastName.value,
      email: email.value,
      password: password.value,
      confirm_password: retypePassword.value,
      subscribe: subscribeNewsletter.value ? 1 : 0,
      redirect: props.redirectUrl || window.location.href,
      ...(token && { recaptchaToken: token }),
    };

    // Store email for email verification (use localStorage for cross-tab access)
    localStorage.setItem("registeredEmail", email.value);
    const response = await authService.register(data);

    // Handle merged account scenario
    if (response?.data?.data?.isMergedAccount && response?.data?.data?.token) {
      const responseData = response.data.data;
      const userData = responseData.user;

      if (userData) {
        await cookiesService.setCookie("authToken", {
          token: responseData.token,
          role: responseData.role,
          ...userData,
        });

        // Update global user state
        await updateUserState(userData);

        console.log("Redirecting merged account user to home");
        // Redirect to home with query param
        router.push({ path: "/", query: { isMergedAccount: "true" } });
        return;
      }
    }

    // Emit success event
    emit("register-success", {
      email: email.value,
      firstName: firstName.value,
      lastName: lastName.value,
    });

    // Emit specific event for going to email verification
    emit("goto-email-verification", email.value);

    // Handle redirect only if enabled
    if (props.enableRedirect) {
      router.push(props.defaultRedirectUrl);
    }
  } catch (error) {
    if (props.showRecaptcha) {
      resetRecaptcha();
      recaptchaToken.value = null;
    }

    let errorMsg = "Registration failed";
    const errorResponse = error?.response?.data?.data;

    if (isAxiosError(error)) {
      if (
        error.response?.data?.error ||
        error.response?.data?.success === false
      ) {
        if (error.response.data?.message.includes("Email already used.")) {
          emailUsed.value = true;
        } else {
          errorMsg = error.response.data.message;
        }
      } else {
        errorMsg = "Registration failed";
      }
    }

    if (
      errorMsg === "Please verify your account" ||
      errorResponse?.redirectToVerification
    ) {
      handleEmailVerificationRequired();
      return;
    }

    requestErrorMessage.value = errorMsg;
    emit("register-error", errorMsg);
  } finally {
    loading.value = false;
  }
}

function signUpWithGoogle() {
  emit("google-register");
  stopGoogleLogin();
  stopGoogleLogin = startGoogleLogin({
    redirectTo: route.query.redirect || props.redirectUrl || "/",
    usePopupMode: props.usePopupMode,
    onSuccess: handleGoogleLoginSuccess,
  });
}

function goToLogin() {
  emit("goto-login");

  if (props.enableRedirect) {
    router.push("/login");
  }
}
</script>

<style scoped>
/* Main Container */
.register-container {
  display: flex;
  height: 100vh;
}

/* Left Panel */
.left-panel {
  width: 50%;
  background-color: var(--primary-blue);
  display: flex;
  align-items: center;
}

.left-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: auto;
}

.logo-link {
  display: flex;
  z-index: 50;
  margin-top: 40px;
  margin-left: 40px;
  position: absolute;
  top: 0;
  left: 0;
}

.logo-link img {
  filter: invert(100%) sepia(100%) saturate(0%) hue-rotate(345deg)
    brightness(104%) contrast(102%);
}

.illustration {
  width: auto;
  max-width: 100%;
}

.promotional-text {
  color: white;
  font-size: 32px;
  font-weight: 600;
}

.login-redirect-button {
  font-size: var(--fs-big);
  font-weight: 600;
  width: 480px;
  height: 48px;
  border-radius: 12px;
  margin-top: 32px;
}

/* Right Panel */
.right-panel {
  width: 50%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
}

.right-panel.full-width {
  width: 100%;
}

/* Language Selector */
.language-selector {
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin-top: 45px;
  margin-right: 40px;
  position: absolute;
  top: 0;
  right: 0;
}

.language-button {
  padding: 8px;
  width: 60px;
  font-size: var(--fs-small);
  cursor: pointer;
  border: none;
}

.language-button-en {
  border-top-left-radius: 10px;
  border-bottom-left-radius: 10px;
}

.language-button-es {
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
}

.language-button.active {
  background: #e00069;
  color: white;
}

.language-button:not(.active) {
  background: #f2f3fb;
  color: #e00069;
}

/* Form Content */
.form-content {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;
  height: 100%;
  padding: 5vh 0;
  box-sizing: border-box;
}

/* Email Used */
.email-used-container {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
}

.email-used-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Title */
.title-container {
  display: flex;
  justify-content: center;
  width: 100%;
}

.title-wrapper {
  width: 508px;
  display: flex;
  flex-direction: column;
  justify-content: start;
  margin-bottom: 24px;
}

.mobile-signin-text {
  display: none;
}

.title {
  font-size: 32px;
  font-weight: 600;
  margin-bottom: 1rem;
  color: var(--primary-blue);
}

/* Name Fields */
.name-fields-container {
  display: flex;
  width: 100%;
  justify-content: center;
}

.register-form-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.name-fields-wrapper {
  display: flex;
  gap: 10px;
  width: 508px;
  justify-content: center;
}

:deep(.name-input) {
  flex: 1;
  color: #3b4648 !important;
  font-size: var(--fs-large) !important;
  line-height: 24px !important;
  text-align: left;
}

:deep(.name-input input) {
  width: 100% !important;
  text-align: left;
  font-size: var(--fs-big) !important;
}

:deep(.name-input .input-wrap) {
  width: 210px !important;
}

/* Form Inputs */
.form-inputs {
  display: flex;
  gap: 24px;
  width: 100%;
  flex-direction: column;
  align-items: center;
}

.input-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
}

:deep(.input-field) {
  width: 508px !important;
  text-align: left;
  color: #3b4648 !important;
  font-size: var(--fs-big) !important;
  line-height: 24px !important;
}

:deep(.input-field input) {
  width: 100% !important;
  text-align: left;
}

:deep(.input-field .input-wrap) {
  width: 508px !important;
}

.error-message {
  text-align: left;
  color: #e00269;
  margin-top: 10px;
  width: 508px;
  word-wrap: break-word;
}

.newsletter-opt-in {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 508px;
  padding: 16px;
  box-sizing: border-box;
  background-color: var(--white-hover-1);
  border-radius: 12px;
  cursor: pointer;
  align-self: center;
}

.newsletter-checkbox-wrap {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
}

.newsletter-checkbox {
  position: absolute;
  inset: 0;
  margin: 0;
  opacity: 0;
  cursor: pointer;
}

.newsletter-checkbox-visual {
  display: block;
  width: 24px;
  height: 24px;
  box-sizing: border-box;
  background-color: var(--white);
  border: 1px solid var(--white-inactive);
  border-radius: 4px;
  pointer-events: none;
}

.newsletter-checkbox:checked + .newsletter-checkbox-visual {
  background-color: var(--primary-purple);
  border-color: var(--primary-purple);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: 14px;
}

.newsletter-checkbox:focus-visible + .newsletter-checkbox-visual {
  outline: 2px solid var(--primary-purple);
  outline-offset: 2px;
}

.newsletter-label {
  color: var(--black-body-text);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.035px;
}

/* reCAPTCHA */
.recaptcha-container {
  width: 508px;
  margin: 10px auto;
  display: flex;
  justify-content: flex-start;
  align-self: center;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
}

.signup-button {
  width: 508px;
}

/* Social Buttons */
.social-buttons {
  display: flex;
  gap: 10px;
  align-items: center;
  width: 508px;
}

.google-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 50px;
  border: 1px solid var(--primary-blue);
  color: var(--primary-blue);
  background-color: white;
  border-radius: 12px;
  font-size: var(--fs-big);
  font-weight: 500;
  flex: 1;
  cursor: pointer;
}

.google-button img {
  width: 20px;
  height: 20px;
}

.facebook-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 50px;
  border: 1px solid var(--border-color);
  background-color: white;
  border-radius: 12px;
  color: var(--border-color);
  font-size: var(--fs-big);
  font-weight: 500;
  flex: 1;
}

.facebook-icon {
  filter: grayscale(100%);
  width: 20px;
  height: 20px;
}

/* Or Divider */
.or-divider {
  display: flex;
  align-items: center;
  width: 508px;
  margin: 20px 0;
  gap: 12px;
}

.or-divider .line {
  flex: 1;
  height: 1px;
  background-color: var(--border-color);
}

.or-divider .text {
  color: #3b4648;
  font-size: var(--fs-small);
  white-space: nowrap;
}

/* Footer Text */
.footer-text-container {
  width: 450px;
}

.footer-text {
  font-size: var(--fs-xs);
  text-align: center;
  color: var(--black-body-text);
  margin-top: 1rem;
}

.signup-footer-links {
  cursor: pointer;
  color: #0056b3;
  text-decoration: none;
}

/* Responsive Styles */
@media (max-width: 768px) {
  .register-container {
    height: 100vh;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 100vh;
  }

  .register-form-container {
    gap: 20px;
  }

  .left-panel {
    display: none;
  }

  .right-panel {
    width: 100%;
    padding: 16px;
    margin-top: 70px;
    justify-content: flex-start;
    overflow-y: visible;
    overflow-x: hidden;
  }

  .form-content {
    justify-content: flex-start;
    padding: 0;
    margin: 0;
    min-height: auto;
  }

  .popup-mode .right-panel {
    margin-top: 0;
  }

  .popup-mode .title-container {
    justify-content: start;
  }

  .mobile-signin-text {
    display: block;
    font-size: var(--fs-small);
    margin-bottom: 16px;
  }

  .mobile-signin-text a {
    color: var(--primary-blue);
    text-decoration: underline;
  }

  .language-selector {
    margin-right: 16px;
    margin-top: 16px;
  }

  .title-wrapper {
    width: 100%;
  }

  .name-fields-wrapper {
    width: 100%;
    flex-direction: column;
    gap: 20px;
  }

  .logo-link {
    margin-left: 16px;
    margin-top: 16px;
  }

  .logo-link img {
    filter: none;
  }

  .language-selector {
    display: none;
  }

  :deep(.name-input) {
    width: 100% !important;
  }

  :deep(.name-input .input-wrap) {
    width: 100% !important;
  }

  .input-wrapper {
    width: 100%;
  }

  :deep(.input-field) {
    width: 100% !important;
  }

  :deep(.input-field .input-wrap) {
    width: 100% !important;
  }

  .error-message {
    width: 100%;
  }

  .newsletter-opt-in {
    width: 100%;
  }

  .recaptcha-container {
    width: 100%;
  }

  .signup-button {
    width: 100%;
  }

  .social-buttons {
    width: 100%;
    flex-direction: column;
  }

  .google-button,
  .facebook-button {
    width: 100%;
    padding: 10px 0px;
  }

  .footer-text-container {
    width: 100%;
  }

  .or-divider {
    width: 100%;
  }

  .login-redirect-button {
    width: 100%;
    max-width: 480px;
  }
}
</style>
